import { Link, NavLink, useNavigate } from "react-router-dom";
import { HotelParHeureBadge } from "@/components/HotelParHeureBadge";
import { useState } from "react";
import { Plus, User, Menu, X, LogOut, MessageCircle, Heart, FileText, Settings, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { useUnreadCount } from "@/lib/useUnreadCount";
import CountrySelector from "@/components/CountrySelector";

const navItems = [
  { to: "/", label: "Accueil" },
  { to: "/annonces", label: "Annonces" },
  { to: "/bonnes-affaires", label: "Bonnes affaires" },
  { to: "/immobilier", label: "Immobilier" },
  { to: "/hotelleries", label: "Hotelleries" },
  { to: "/annonces?category=Vehicules", label: "Vehicules" },
  { to: "/hotel-par-heure", label: "Hôtel par Heure", badge: true },
  { to: "/annonces?category=Entreprise", label: "Entreprise" },
  { to: "/favoris", label: "Favoris" },
  { to: "/abonnements", label: "Abonnements" },
  { to: "/dashboard", label: "Dashboard" },
  { to: "/contact", label: "Contact" },
  { to: "/cgu", label: "CGU" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const { count: unread } = useUnreadCount();

  const close = () => setOpen(false);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-[var(--line)]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" onClick={close}>
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-400 text-white grid place-items-center font-bold text-xl shadow-md">F</div>
          <div className="leading-tight">
            <div className="font-bold text-[15px] text-[var(--ink)]">fodindin</div>
            <div className="text-[11px] text-[var(--ink-soft)]">Petites annonces premium</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((it) => it.badge ? <HotelParHeureBadge key={it.to} /> : (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-[14px] font-semibold rounded-full transition-colors ${
                  isActive ? "text-orange-600" : "text-[var(--ink)] hover:text-orange-600"
                }`
              }
              end={it.to === "/"}
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden sm:flex items-center gap-2">
          <CountrySelector />
          <button
            onClick={() => navigate("/deposer")}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="nav-deposer-btn"
          >
            <Plus size={16} /> <span className="hidden md:inline">Deposer une annonce</span><span className="md:hidden">Deposer</span>
          </button>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className="relative flex items-center gap-2 btn-secondary"
                data-testid="nav-account-btn"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <User size={16} />
                )}
                <span className="hidden md:inline max-w-[120px] truncate">{user.name || user.email}</span>
                {unread > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold grid place-items-center shadow"
                    data-testid="nav-unread-badge"
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 glass-card p-2 shadow-xl z-50" data-testid="account-menu">
                  <MenuLink to="/mon-compte" icon={User} label="Mon compte" onClose={() => setMenuOpen(false)} testid="menu-mon-compte" />
                  <MenuLink to="/mes-annonces" icon={FileText} label="Mes annonces" onClose={() => setMenuOpen(false)} testid="menu-mes-annonces" />
                  <MenuLink to="/favoris" icon={Heart} label="Favoris" onClose={() => setMenuOpen(false)} testid="menu-favoris" />
                  <MenuLink to="/messages" icon={MessageCircle} label="Messages" onClose={() => setMenuOpen(false)} testid="menu-messages" badge={unread} />
                  <MenuLink to="/parametres" icon={Settings} label="Parametres" onClose={() => setMenuOpen(false)} testid="menu-parametres" />
                  <MenuLink to="/admin" icon={ShieldCheck} label="Tableau de bord" onClose={() => setMenuOpen(false)} testid="menu-admin-dashboard" />
                  <MenuLink to="/admin/annonces" icon={Settings} label="Gestion annonces" onClose={() => setMenuOpen(false)} testid="menu-admin-annonces" />
                  <MenuLink to="/admin/hotels" icon={Settings} label="Gestion hotels" onClose={() => setMenuOpen(false)} testid="menu-admin-hotels" />
                  <button
                    onClick={() => { setMenuOpen(false); logout(); }}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 inline-flex items-center gap-2"
                    data-testid="menu-logout"
                  >
                    <LogOut size={14} /> Se deconnecter
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={login}
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="nav-connexion-btn"
            >
              <User size={16} /> Connexion
            </button>
          )}
        </div>

        {/* Mobile hamburger */}
        <div className="lg:hidden flex items-center gap-2">
          <CountrySelector compact />
          <button
            className="btn-secondary !p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="nav-hamburger"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {open && (
        <div className="lg:hidden border-t border-[var(--line)] bg-white/95 backdrop-blur-md" data-testid="mobile-panel">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {navItems.map((it) => it.badge ? <HotelParHeureBadge key={it.to} mobile onClick={close} /> : (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={close}
                end={it.to === "/"}
                data-testid={`mnav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-xl font-semibold ${isActive ? "bg-orange-50 text-orange-600" : "text-[var(--ink)]"}`
                }
              >
                {it.label}
              </NavLink>
            ))}
            <div className="border-t border-[var(--line)] mt-2 pt-3 flex flex-col gap-2">
              <button onClick={() => { close(); navigate("/deposer"); }} className="btn-primary w-full justify-center inline-flex items-center gap-2" data-testid="mnav-deposer">
                <Plus size={16} /> Deposer une annonce
              </button>
              {user ? (
                <>
                  <button onClick={() => { close(); navigate("/mon-compte"); }} className="btn-secondary w-full" data-testid="mnav-mon-compte">Mon compte</button>
                  <button onClick={() => { close(); logout(); }} className="btn-secondary w-full !text-red-600" data-testid="mnav-logout">Se deconnecter</button>
                </>
              ) : (
                <button onClick={() => { close(); login(); }} className="btn-secondary w-full" data-testid="mnav-connexion">Connexion / Inscription</button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function MenuLink({ to, icon: Icon, label, onClose, testid, badge }) {
  return (
    <NavLink
      to={to}
      onClick={onClose}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${isActive ? "bg-orange-50 text-orange-600" : "text-[var(--ink)] hover:bg-orange-50/60"}`
      }
    >
      <Icon size={14} /> <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold grid place-items-center">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}
